import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import Modal from './Modal';
import Room from './Room';
import './LobbyPage.css';

const LobbyPage = () => {
  const navigate = useNavigate();
  const { state, actions } = useGame();
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [showJoinRoomModal, setShowJoinRoomModal] = useState(false);
  const [showPrivateJoinModal, setShowPrivateJoinModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomName, setRoomName] = useState('');
  const [roomId, setRoomId] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isPrivate, setIsPrivate] = useState(false);
  const [password, setPassword] = useState('');
  const [privateJoinPassword, setPrivateJoinPassword] = useState('');
  const playerDisplayName = state.player.name || '미설정';

  // 컴포넌트 마운트 시 로컬 스토리지에서 플레이어 이름 불러오기
  useEffect(() => {
    const savedPlayerName = localStorage.getItem('playerName');
    if (savedPlayerName) {
      actions.setPlayer({ name: savedPlayerName });
    }
  }, [actions.setPlayer]);

  // 방 목록 조회 및 실시간 업데이트
  useEffect(() => {
    // 컴포넌트 마운트 시 방 목록 조회
    actions.getRooms();
  }, [actions.getRooms]);

  // 방 생성/참여 성공 시 방 페이지로 이동
  useEffect(() => {
    if (state.room.id && state.ui.isConnected) {
      navigate('/room');
    }
  }, [state.room.id, state.ui.isConnected, navigate]);

  // 방 이름 유효성 검사
  const validateRoomName = () => {
    if (!roomName.trim()) {
      setFormErrors({ roomName: '방 이름을 입력해주세요.' });
      return false;
    }
    if (roomName.trim().length < 2) {
      setFormErrors({ roomName: '방 이름은 최소 2자 이상이어야 합니다.' });
      return false;
    }
    if (roomName.trim().length > 20) {
      setFormErrors({ roomName: '방 이름은 최대 20자까지 가능합니다.' });
      return false;
    }
    setFormErrors({});
    return true;
  };

  // 방 ID 유효성 검사
  const validateRoomId = () => {
    if (!roomId.trim()) {
      setFormErrors({ roomId: '방 ID를 입력해주세요.' });
      return false;
    }
    setFormErrors({});
    return true;
  };

  // 방 생성 모달 열기
  const openCreateRoomModal = () => {
    setShowCreateRoomModal(true);
  };

  // 방 참여 모달 열기
  const openJoinRoomModal = () => {
    setShowJoinRoomModal(true);
  };

  // 방 생성 처리
  const handleCreateRoom = () => {
    if (!validateRoomName()) return;

    actions.setPlayer({
      name: playerDisplayName,
      isHost: true
    });

    actions.createRoom({
      name: roomName.trim(),
      maxPlayers: 8,
      isPrivate,
      password: isPrivate ? password : ''
    });
  };

  // 방 참여 처리
  const handleJoinRoom = (roomId, roomPassword = '') => {
    actions.setPlayer({
      name: playerDisplayName,
      isHost: false
    });

    actions.joinRoom(roomId, {
      name: playerDisplayName,
      password: roomPassword
    });
  };

  // 비공�?방 참여 버튼 처리
  const handleRoomJoinClick = (room) => {
    if (room.isPrivate) {
      setSelectedRoom(room);
      setPrivateJoinPassword('');
      setFormErrors({});
      setShowPrivateJoinModal(true);
      return;
    }

    handleJoinRoom(room.id);
  };

  // 방 ID로 참여 처리
  const handleJoinRoomById = () => {
    if (!validateRoomId()) return;
    handleJoinRoom(roomId.trim(), password.trim());
  };

  // ??? ? ?? ??
  const handlePrivateRoomJoin = () => {
    if (!selectedRoom) return;

    if (!privateJoinPassword.trim()) {
      setFormErrors({ joinPassword: '비밀번호를 입력해주세요.' });
      return;
    }

    handleJoinRoom(selectedRoom.id, privateJoinPassword.trim());
  };

  // 에러 메시지 닫기
  const closeError = () => {
    actions.clearError();
  };

  // 모달 닫기
  const closeModals = () => {
    setShowCreateRoomModal(false);
    setShowJoinRoomModal(false);
    setShowPrivateJoinModal(false);
    setSelectedRoom(null);
    setRoomName('');
    setRoomId('');
    setPassword('');
    setPrivateJoinPassword('');
    setIsPrivate(false);
    setFormErrors({});
  };

  // 방 목록 새로고침
  const refreshRooms = () => {
    actions.getRooms();
  };

  return (
    <div className="lobby-container">
      <img
        src="/resource/lobby/logo.png"
        alt="Death by Clevi 로비"
        className="lobby-logo"
      />
      <div className="lobby-content">
        <div className="player-name-display inside">
          <span className="player-name-label">플레이어 이름</span>
          <span className="player-name-value">{playerDisplayName}</span>
        </div>

        <div className="rooms-section">
          <div className="rooms-header">
            <h2>참여 가능한 방</h2>
            <button
              className="refresh-btn"
              onClick={refreshRooms}
              disabled={!state.ui.isConnected}
              aria-label="방 목록 새로고침"
            >
              {state.ui.isLoading ? <LoadingSpinner size="small" /> : null}
            </button>
          </div>
          {state.ui.isLoading ? (
            <LoadingSpinner message="방 목록을 불러오는 중..." />
          ) : (
            <div className="rooms-list">
              {state.roomsList.length > 0 ? (
                state.roomsList
                  .filter(room => !room.gameStarted) // 시작되지 않은 게임만 필터링
                  .map((room) => (
                  <div key={room.id} className="room-card">
                    <div className="room-info">
                      <h3>{room.name}</h3>
                      <div className="room-details">
                        <span className="room-id">ID: {room.id}</span>
                        <span className="player-count">
                          {room.playerCount || 0}/{room.maxPlayers} 명
                        </span>
                        {room.isPrivate && (
                          <span className="private-room">비공개</span>
                        )}
                      </div>
                    </div>
                    <button
                      className="join-btn"
                      onClick={() => handleRoomJoinClick(room)}
                      disabled={(room.playerCount || 0) >= room.maxPlayers}
                      aria-label={(room.playerCount || 0) >= room.maxPlayers ? '만원' : '참여'}
                    >
                      {(room.playerCount || 0) >= room.maxPlayers ? '만원' : '참여'}
                    </button>
                  </div>
                ))
              ) : (
                <div className="no-rooms">
                  <p>현재 참여 가능한 방이 없습니다.</p>
                  <p>새로운 방을 만들어보세요!</p>
                </div>
              )}
            </div>
          )}
        </div>

        {state.ui.error && (
          <ErrorMessage
            message={state.ui.error}
            onClose={closeError}
          />
        )}

        <div className="lobby-actions">
          <button
            className="create-room-btn"
            onClick={openCreateRoomModal}
            disabled={!state.ui.isConnected}
            aria-label="방 만들기"
          >
          </button>
          <button
            className="join-room-btn"
            onClick={openJoinRoomModal}
            disabled={!state.ui.isConnected}
            aria-label="방 ID로 참여"
          >
          </button>
        </div>
      </div>

      {/* 방 생성 모달 */}
      <Modal
        isOpen={showCreateRoomModal}
        onClose={closeModals}
        title="방 만들기"
        size="small"
      >
        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="roomName">방 이름</label>
            <input
              type="text"
              id="roomName"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="방 이름을 입력하세요"
              maxLength={20}
              autoComplete="off"
            />
            {formErrors.roomName && (
              <div className="error-text">{formErrors.roomName}</div>
            )}
          </div>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
              />
              비공개 방
            </label>
          </div>
          
          {isPrivate && (
            <div className="form-group">
              <label htmlFor="password">비밀번호</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호를 입력하세요"
              />
            </div>
          )}
          
          <div className="modal-actions">
            <button
              className="cancel-btn create-room-cancel-btn modal-image-btn"
              onClick={closeModals}
              aria-label="취소"
            >
              <span className="sr-only">취소</span>
            </button>
            <button
              className="confirm-btn create-room-confirm-btn modal-image-btn"
              onClick={handleCreateRoom}
              disabled={state.ui.isLoading}
              aria-label="방 만들기"
            >
              {state.ui.isLoading ? (
                <LoadingSpinner size="small" message="" />
              ) : (
                <span className="sr-only">방 만들기</span>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* 방 ID 참여 모달 */}
      <Modal
        isOpen={showJoinRoomModal}
        onClose={closeModals}
        title="방에 참여하기"
        size="small"
      >
        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="roomId">방 ID</label>
            <input
              type="text"
              id="roomId"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="방 ID를 입력해주세요."
              autoComplete="off"
            />
            {formErrors.roomId && (
              <div className="error-text">{formErrors.roomId}</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="joinPassword">비밀번호 (필요 시)</label>
            <input
              type="password"
              id="joinPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력해주세요."
            />
          </div>
          
          <div className="modal-actions">
            <button
              className="cancel-btn join-room-cancel-btn modal-image-btn"
              onClick={closeModals}
              aria-label="취소"
            >
              <span className="sr-only">취소</span>
            </button>
            <button
              className="confirm-btn join-room-confirm-btn modal-image-btn"
              onClick={handleJoinRoomById}
              disabled={state.ui.isLoading}
            >
              {state.ui.isLoading ? (
                <LoadingSpinner size="small" />
              ) : (
                <span className="sr-only">참여</span>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* 비공개 방 참여 모달 */}
      <Modal
        isOpen={showPrivateJoinModal}
        onClose={closeModals}
        title="비공개 방 참여"
        size="small"
      >
        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="privateRoomName">방 이름</label>
            <div className="static-text-field" id="privateRoomName">
              {selectedRoom?.name || '이름 없음'}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="privateRoomId">방 ID</label>
            <div className="static-text-field" id="privateRoomId">
              {selectedRoom?.id || 'ID 없음'}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="privateRoomPassword">비밀번호</label>
            <input
              type="password"
              id="privateRoomPassword"
              value={privateJoinPassword}
              onChange={(e) => setPrivateJoinPassword(e.target.value)}
              placeholder="비밀번호를 입력해주세요."
              autoComplete="off"
            />
            {formErrors.joinPassword && (
              <div className="error-text">{formErrors.joinPassword}</div>
            )}
          </div>

          <div className="modal-actions">
            <button
              className="cancel-btn join-room-cancel-btn modal-image-btn"
              onClick={closeModals}
              aria-label="취소"
            >
              <span className="sr-only">취소</span>
            </button>
            <button
              className="confirm-btn join-room-confirm-btn modal-image-btn"
              onClick={handlePrivateRoomJoin}
              disabled={state.ui.isLoading}
            >
              {state.ui.isLoading ? (
                <LoadingSpinner size="small" />
              ) : (
                <span className="sr-only">참여</span>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LobbyPage;
